import { useEffect, useMemo } from "react";
import { Tag } from "antd";
import useGet from "../common/useGet";
import useSearchOptions from "../common/useSearchOptions";
import { StatusTag } from "../../components/common/registerUtils";
import {
  API_ASSET_CATEGORIES,
  API_ASSETS,
  API_DISTRICTS,
  API_USERS,
} from "../../utils/apis";
import {
  ACTION_APPROVE,
  ACTION_REJECT,
  APPROVED,
  PENDING,
  REJECTED,
} from "../../utils/constants";
import {
  addSelectItem,
  formatDateTime,
  useGetUserPermissions,
  userSelectItem,
} from "../../utils/helpers";

// ENUMS
export const ASSET_TYPES = {
  fixed_asset: "Fixed Asset",
  equipment: "Equipment",
};

export const ASSET_CONDITIONS = {
  new: "New",
  good: "Good",
  fair: "Fair",
  bad: "Bad",
  damaged: "Damaged",
};

export const ASSET_AVAILABLE = "available";
export const ASSET_ASSIGNED = "assigned";
export const ASSET_DISPOSED = "disposed";

export const ASSET_STATUSES = {
  [ASSET_AVAILABLE]: "Available",
  [ASSET_ASSIGNED]: "Assigned",
  under_maintenance: "Under Maintenance",
  lost: "Lost",
  damaged: "Damaged",
  retired: "Retired",
  [ASSET_DISPOSED]: "Disposed",
};

export const DISPOSAL_TYPES = {
  sale: "Sale",
  donation: "Donation",
  write_off: "Write Off",
  retirement: "Retirement",
};

export const APPROVAL_STATUSES = {
  [PENDING]: "Pending",
  [APPROVED]: "Approved",
  [REJECTED]: "Rejected",
};

// Cross-resource rules: which asset statuses block which actions
const NON_TRANSFERABLE = ["lost", "retired", ASSET_DISPOSED];
export const canTransferAsset = (asset: any) =>
  !!asset && !NON_TRANSFERABLE.includes(asset?.status);
export const isDisposed = (asset: any) => asset?.status === ASSET_DISPOSED;

// { value: label } -> select options (form fields)
export const toSelectItems = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) =>
    addSelectItem({ value, label }),
  );

// { value: label } -> table filter options
export const toFilterOptions = (labels: Record<string, string>) =>
  Object.entries(labels).map(([id, name]) => ({ id, name }));

export const userFullName = (user: any) =>
  user ? `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() : null;

export const assetLabel = (item: any) => {
  const details = item?.asset_details;
  if (details) return `${details?.asset_id} - ${details?.name}`;
  return item?.asset_name ?? item?.asset;
};

// GenericView renders falsy values as "Empty"; keep zeros visible
export const showNumber = (value: any) =>
  value === null || value === undefined ? null : String(value);

const ASSET_STATUS_COLORS: Record<string, string> = {
  available: "green",
  assigned: "blue",
  under_maintenance: "orange",
  lost: "red",
  damaged: "volcano",
  retired: "default",
  disposed: "default",
};

export const AssetStatusTag = ({ status }: { status: string }) =>
  status ? (
    <Tag color={ASSET_STATUS_COLORS[status]} style={{ background: "none" }}>
      {ASSET_STATUSES[status] ?? status}
    </Tag>
  ) : null;

// Read-only snapshot of the selected asset shown on the workflow forms
export const AssetSummary = ({
  asset,
  warning,
}: {
  asset: any;
  warning?: string;
}) =>
  asset ? (
    <div className="font12">
      <p className="mb-1">
        <strong>Location:</strong> {asset?.location_name} &nbsp;|&nbsp;{" "}
        <strong>Condition:</strong>{" "}
        {ASSET_CONDITIONS[asset?.condition] ?? asset?.condition} &nbsp;|&nbsp;{" "}
        <strong>Quantity:</strong> {asset?.quantity} &nbsp;|&nbsp;{" "}
        <strong>Status:</strong> <AssetStatusTag status={asset?.status} />
      </p>
      {warning && (
        <p className="mb-0" style={{ color: "red" }}>
          {warning}
        </p>
      )}
    </div>
  ) : null;

// Common audit fields shared by every asset resource
export const auditFields = (item: any) => ({
  created_by: userFullName(item?.created_by),
  created_at: formatDateTime(item?.created_at),
  updated_by: userFullName(item?.updated_by),
  updated_at: formatDateTime(item?.updated_at),
});

// Approval details for transfers, stock counts and disposals
export const approvalSection = (item: any) => ({
  title: "Approval",
  visible: item?.status && item?.status !== PENDING,
  fields: {
    status: <StatusTag status={item?.status} />,
    actioned_by: userFullName(item?.approved_by) ?? item?.approved_by,
    actioned_at: formatDateTime(item?.approved_at),
    ...(item?.status === REJECTED
      ? { rejection_reason: item?.rejection_reason }
      : {}),
  },
});

export const useAssetPermissions = () => {
  const permissions: string[] = useGetUserPermissions();
  return (permission: string) => permissions.includes(permission);
};

// Export config consumed by RegisterLayoutHandler. The route status (e.g.
// /register/asset_transfers/pending) is forwarded so exports match the list.
export const assetExport = (api: string, name: string) => ({
  api,
  fileName: `${name}_${new Date().toISOString().split("T")[0]}.xlsx`,
  withStatus: true,
});

export const createdAtFilter = {
  type: "date",
  dateRangeNames: { startDateKey: "start_date", endDateKey: "end_date" },
};

// OPTION LOADERS
export const useDistrictOptions = () => {
  const { loadData, data, loading } = useGet();

  useEffect(() => {
    loadData({ api: API_DISTRICTS });
  }, []);

  const options = useMemo(
    () =>
      (Array.isArray(data) ? data : data?.results || []).map((district: any) =>
        addSelectItem({ label: district?.name, value: district?.id }),
      ),
    [data],
  );

  const names = useMemo(
    () =>
      Object.fromEntries(options.map((item: any) => [item.value, item.name])),
    [options],
  );

  return { options, names, loading };
};

export const useCategoryOptions = () => {
  const { loadData, data, loading } = useGet();

  useEffect(() => {
    loadData({ api: API_ASSET_CATEGORIES, params: { page_size: 50 } });
  }, []);

  const options = (data?.results || data || []).map((category: any) =>
    addSelectItem({ label: category?.name, value: category?.id }),
  );

  return { options, loading };
};

// Pass the selected user's record so it stays labelled while editing
export const useUserOptions = (selectedUser?: any) =>
  useSearchOptions({
    api: API_USERS,
    searchParam: "first_name",
    toOption: userSelectItem,
    selected: selectedUser?.id ? userSelectItem(selectedUser) : undefined,
  });

const assetOption = (item: any) =>
  addSelectItem({
    label: `${item?.asset_id} - ${item?.name}`,
    value: item?.id,
  });

// Asset picker with name search, plus the full record of the selected asset
// so forms can show its current location/status/quantity.
export const useAssetPicker = (assetId?: string) => {
  const { loadData: loadAsset, data } = useGet();

  useEffect(() => {
    if (assetId) loadAsset({ id: assetId, api: API_ASSETS });
  }, [assetId]);

  const asset = data?.id === assetId ? data : null;

  const search = useSearchOptions({
    api: API_ASSETS,
    searchParam: "name",
    params: { page_size: 50, transferable: true },
    toOption: assetOption,
    selected: asset ? assetOption(asset) : undefined,
  });

  return { search, asset };
};

// APPROVAL ACTION FORM (transfers, stock counts, disposals)
export const createAssetApprovalActionForm = (
  api: string,
  permission: string,
) =>
  function useAssetApprovalActionForm({ form, data }) {
    const permissions = useGetUserPermissions();

    // Status only moves through approve/reject, and only while pending
    if (data?.status !== PENDING || !permissions.includes(permission)) {
      return { fields: [], onSubmit: null, enableSubmit: false };
    }

    const fields = [
      {
        field_name: "action",
        label: "Decision",
        type: "select",
        props: { required: true },
        options_data: [
          addSelectItem({ label: "Approve", value: ACTION_APPROVE }),
          addSelectItem({ label: "Reject", value: ACTION_REJECT }),
        ],
      },
      {
        field_name: "notes",
        label: form?.action === ACTION_REJECT ? "Rejection Reason" : "Notes",
        type: "textarea",
        props: { required: form?.action === ACTION_REJECT },
      },
    ];

    function onSubmit(payload: any, id: any) {
      if (id && payload?.action) {
        return {
          api: `${api}${id}/${payload.action}/`,
          method: "POST",
          params: { notes: payload?.notes || "" },
        };
      }
    }

    const enableSubmit = !!form?.action;

    return { fields, onSubmit, enableSubmit };
  };
